import React from 'react';
import { Box, IconButton, CircularProgress, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ShareIcon from '@mui/icons-material/Share';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { ViewKontakt } from '../../api';
import AudioDownloadButton from './AudioDownloadButton';
import PdfDownloadButton from './PdfDownloadButton';
import StopTwilioCallButton from "./table/StopTwilioCallButton";
import {useSnackbar} from "notistack";

interface KontaktActionButtonsProps {
    kontakt: ViewKontakt;
    kontaktaufnahmeId?: string | null | undefined;
    kontaktAudioDatei?: string | null | undefined;
    kontaktVorname?: string | null | undefined;
    kontaktNachname?: string | null | undefined;
    aufgabeId?: string;
    audioTitle?: string;
    pdfTitle?: string;
    deleteTitle?: string;
    shareTitle?: string;
    shareCopiedTitle?: string;
    onDelete?: (kontakt: ViewKontakt) => void;
    size?: 'small' | 'medium' | 'large';
    color?: 'inherit' | 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    gap?: number;
    showDelete?: boolean;
}

const KontaktActionButtons: React.FC<KontaktActionButtonsProps> = ({
    kontakt,
    kontaktaufnahmeId,
    kontaktAudioDatei,
    kontaktVorname,
    kontaktNachname,
    aufgabeId,
    audioTitle,
    pdfTitle,
    deleteTitle,
    shareTitle,
    shareCopiedTitle,
    onDelete,
    size = 'small',
    color = 'default',
    gap = 1,
    showDelete = true
}) => {
    const {enqueueSnackbar} = useSnackbar();

    // Only render if kontaktaufnahmeId exists
    if (!kontaktaufnahmeId) {
        return null;
    }

    const handleShare = () => {
        if (!aufgabeId || !kontaktaufnahmeId) return;
        const url = `${window.location.origin}/aufgaben/${aufgabeId}/kontakte?kontaktaufnahmeId=${kontaktaufnahmeId}`;
        navigator.clipboard.writeText(url).then(() => {
            enqueueSnackbar(shareCopiedTitle ?? "Link copied", {variant: "success"});
        });
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap }}>
            <AudioDownloadButton
                kontaktaufnahmeId={kontaktaufnahmeId}
                kontaktVorname={kontaktVorname}
                kontaktNachname={kontaktNachname}
                kontaktAudioDatei={kontaktAudioDatei}
                title={audioTitle}
                size={size}
                color={color}
            />
            <PdfDownloadButton
                kontaktaufnahmeId={kontaktaufnahmeId}
                title={pdfTitle}
                size={size}
                color={color}
            />
            {aufgabeId && (
                <Tooltip title={shareTitle ?? "Share"}>
                    <IconButton
                        size={size}
                        color={color}
                        onClick={handleShare}
                    >
                        <ShareIcon />
                    </IconButton>
                </Tooltip>
            )}
            {showDelete && onDelete && (
                <IconButton
                    size={size}
                    color="error"
                    onClick={() => onDelete(kontakt)}
                    title={deleteTitle}
                >
                    <DeleteIcon />
                </IconButton>
            )}
        </Box>
    );
};

export interface ActionButtonDefinition {
    key: string;
    icon: React.ReactElement;
    label: string;
    onClick: () => void;
    disabled?: boolean;
    color?: 'inherit' | 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

export const ActionButtonsBar: React.FC<{
    actions: ActionButtonDefinition[];
    size?: 'small' | 'medium' | 'large';
}> = ({ actions, size = 'medium' }) => (
    <>
        {actions.map(({ key, icon, label, onClick, disabled, color }) => (
            <Tooltip key={key} title={label}>
                <span>
                    <IconButton color={color ?? 'default'} onClick={onClick} disabled={disabled} size={size}>
                        {icon}
                    </IconButton>
                </span>
            </Tooltip>
        ))}
    </>
);

const ICON_SIZE = 20;

export const getKontaktCoreActionDefinitions = (
    onAudioDownload: () => void,
    onPdfDownload: () => void,
    onReschedule: () => void,
    onRecoverAnalysis: () => void,
    onDelete: () => void,
    translations: {
        downloadAudio: string;
        downloadPdf: string;
        reschedule: string;
        recoverAnalysis: string;
        delete: string;
        share?: string;
    },
    options?: {
        isAudioLoading?: boolean;
        isPdfLoading?: boolean;
        includeAudio?: boolean;
        includeRecoverAnalysis?: boolean;
        disabled?: boolean;
        onShare?: () => void;
    }
): ActionButtonDefinition[] => {
    const isAudioLoading = options?.isAudioLoading ?? false;
    const isPdfLoading = options?.isPdfLoading ?? false;
    const includeAudio = options?.includeAudio ?? true;
    const includeRecoverAnalysis = options?.includeRecoverAnalysis ?? true;
    const disabled = options?.disabled ?? false;
    const isAnyLoading = isAudioLoading || isPdfLoading;

    const definitions: ActionButtonDefinition[] = [];

    if (includeRecoverAnalysis) {
        definitions.push({
            key: 'recover-analysis',
            icon: <SettingsBackupRestoreIcon />,
            label: translations.recoverAnalysis,
            onClick: onRecoverAnalysis,
            color: 'primary',
            disabled: disabled || isAnyLoading,
        });
    }

    if (includeAudio) {
        definitions.push({
            key: 'audio',
            icon: isAudioLoading ? <CircularProgress size={ICON_SIZE} /> : <AudioFileIcon />,
            label: translations.downloadAudio,
            onClick: onAudioDownload,
            color: 'primary',
            disabled: disabled || isAudioLoading,
        });
    }

    definitions.push({
        key: 'pdf',
        icon: isPdfLoading ? <CircularProgress size={ICON_SIZE} /> : <PictureAsPdfIcon />,
        label: translations.downloadPdf,
        onClick: onPdfDownload,
        color: 'primary',
        disabled: disabled || isPdfLoading,
    });

    if (options?.onShare && translations.share) {
        definitions.push({
            key: 'share',
            icon: <ShareIcon />,
            label: translations.share,
            onClick: options.onShare,
            color: 'primary',
            disabled: disabled || isAnyLoading,
        });
    }

    definitions.push({
        key: 'reschedule',
        icon: <ScheduleIcon />,
        label: translations.reschedule,
        onClick: onReschedule,
        color: 'primary',
        disabled: disabled || isAnyLoading,
    });

    definitions.push({
        key: 'delete',
        icon: <DeleteIcon />,
        label: translations.delete,
        onClick: onDelete,
        color: 'error',
        disabled: disabled || isAnyLoading,
    });

    return definitions;
};

export const getKontaktBulkActionDefinitions = (
    onDelete: () => void,
    onReschedule: () => void,
    onRecoverAnalysis: () => void,
    onAudioDownload: () => void,
    onPdfDownload: () => void,
    translations: {
        delete: string;
        reschedule: string;
        recoverAnalysis: string;
        downloadAudio: string;
        downloadPdf: string;
    },
    disabled = false
): ActionButtonDefinition[] =>
    getKontaktCoreActionDefinitions(onAudioDownload, onPdfDownload, onReschedule, onRecoverAnalysis, onDelete, translations, {
        disabled,
    });

export const getKontaktActionItems = (
    kontakt: ViewKontakt,
    onAudioDownload: (kontakt: ViewKontakt) => void,
    onPdfDownload: (kontakt: ViewKontakt) => void,
    onDelete: (kontakt: ViewKontakt) => void,
    onReschedule: (kontakt: ViewKontakt) => void,
    onRecoverAnalysis: (kontakt: ViewKontakt) => void,
    translations: {
        downloadAudio: string;
        downloadPdf: string;
        delete: string;
        reschedule: string;
        recoverAnalysis: string;
        share?: string;
    },
    loadingState?: {
        isAudioLoading?: boolean;
        isPdfLoading?: boolean;
    },
    onShare?: (kontakt: ViewKontakt) => void,
): React.ReactElement[] => {
    const actions: React.ReactElement[] = [];

    // Add stop call button if call is ongoing
    if (kontakt.kontaktaufnahmeId && kontakt.kontaktaufnahmeStatus === 'LAUFEND') {
        actions.push(
            <StopTwilioCallButton
                key="stop-call"
                id={kontakt.kontaktaufnahmeId}
                isGrid={true}
            />
        );
    }

    const definitions = getKontaktCoreActionDefinitions(
        () => onAudioDownload(kontakt),
        () => onPdfDownload(kontakt),
        () => onReschedule(kontakt),
        () => onRecoverAnalysis(kontakt),
        () => onDelete(kontakt),
        translations,
        {
            isAudioLoading: loadingState?.isAudioLoading,
            isPdfLoading: loadingState?.isPdfLoading,
            includeAudio: !!kontakt.kontaktaufnahmeAudioDatei,
            includeRecoverAnalysis: !!kontakt.kontaktaufnahmeAudioDatei,
            onShare: onShare ? () => onShare(kontakt) : undefined,
        }
    );

    definitions.forEach(({ key, icon, label, onClick, disabled }) => {
        actions.push(
            <GridActionsCellItem
                key={key}
                icon={icon}
                label={label}
                onClick={onClick}
                showInMenu={false}
                disabled={disabled}
                sx={key === 'delete' ? { color: 'error.main' } : undefined}
            />
        );
    });

    return actions;
};

export default KontaktActionButtons;

